import { useQuery } from '@apollo/client/react';
import { type FunctionComponent } from 'react';
import { configureRoute } from '../../../utils/routes/configure-route';
import { type HomePageQuery, type HomePageQueryVariables } from '../../__generated__/graphql-client-types';
import { PageLoading } from '../../components/common-components/loading/loading.component';
import { PageContainer } from '../../components/common-components/page/page-container/page-container.component';
import { FiveHundred } from '../../components/error-components/500/500.component';
import { isSalesSection } from '../../components/home-components/home-common.helpers';
import { GenerateInViewHomeSection } from '../../components/home-components/home-section-factory/home-section-factory.component';
import { DEFAULT_META_DESCRIPTION, SITE_NAME } from '../../constants/general';
import { HOMEPAGE_LINK } from '../../constants/links';
import { useTrackPageView } from '../../hooks/analytics/analytics.hooks';
import { useDyRecommendationContext, DyRecommendationContextType } from '../../hooks/dynamic-yield/dynamic-yield-client.hooks';
import { useRouterLocation } from '../../hooks/routing/location.hook';
import { HOMEPAGE } from '../../queries/homepage/homepage.queries';
import { visuallyHidden } from './home.css';

export const Home: FunctionComponent = () => {
	const { search } = useRouterLocation();
	const params = new URLSearchParams(search);
	const contentItemId = params.get('contentitemid');
	const { data, error, loading } = useQuery<HomePageQuery, HomePageQueryVariables>(HOMEPAGE, {
		fetchPolicy: 'cache-first',
		variables: { input: { ...(contentItemId ? { contentItemId } : {}) } }
	});
	const homepageContent = data?.homepage;

	useTrackPageView({ pageName: 'home' }, { page: 'home' }, loading);
	useDyRecommendationContext({ type: DyRecommendationContextType.HOME_PAGE });

	if (loading) {
		return <PageLoading />;
	} else if (error) {
		return <FiveHundred />;
	}
	return (
		<div className="bg-theme-white">
			{homepageContent && (
				<PageContainer
					canonicalURL={HOMEPAGE_LINK}
					pageTitle="Bathroom, Kitchen - Faucets, Sinks, Hardware - Build with Ferguson"
					metaDescription={DEFAULT_META_DESCRIPTION}
					layout="minimized"
					showUpdateLocation={true}>
					<>
						<ul className="list ma0 pa0 flex flex-column justify-between items-center w-100" data-testid="sections">
							{homepageContent?.sections.map((section, index) => (
								<li
									className={`flex flex-column w-100 pa0 mh0 mb0 ${
										isSalesSection(section) ? 'mt0' : index === 0 ? 'mt3' : 'mt6'
									}
									`}
									key={index}>
									<GenerateInViewHomeSection {...section} />
								</li>
							))}
						</ul>
					</>
				</PageContainer>
			)}
			<h1 className={`overflow-hidden absolute bn pa0 ${visuallyHidden}`}>
				Find Bathroom and Kitchen Sinks, Faucets and Hardware on {SITE_NAME}
			</h1>
		</div>
	);
};

export const RoutedHomePage = configureRoute({ path: HOMEPAGE_LINK, ddRum: { rumViewName: 'Home' } }, Home);
